import * as React from "react";
import {useCallback, useEffect, useReducer, useRef} from "react";
import {TableVirtuoso} from "react-virtuoso";
import makeStyles from "@mui/styles/makeStyles";
import {FlexHeader, FlexHeaderData} from "./FlexHeader";
import {FlexTabs} from "./FlexTab";
import {FlexCommand, FlexFilterValue, FlexListColumn, FlexListProps, FlexTab} from "./FlexTypes";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import {
   Box,
   Button,
   CircularProgress,
   Dialog,
   DialogContent,
   IconButton,
   LinearProgress,
   ListItemIcon,
   ListItemText,
   Menu,
   MenuItem
} from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {PubSub} from "../PubSub";
import {atom, useAtom, useAtomValue} from 'jotai';
import {atomFamily} from 'jotai/utils';
import {buildGraphQLFilter} from "./FlexUtility";
import {useSearchParams} from 'react-router-dom';
import {applyURLParamsToFlexList, filtersMatch, filtersToFilterObjects, getFlexListURLParams, updateSearchParams} from "./FlexUrlSerializer";
import {loadCustomColumns} from "./FlexColumnPersistence";

const CUSTOM_TAB_ID = '__custom__';

/**
 * Find a tab whose predefined filters match the given filters
 */
function findMatchingTab(filters: FlexFilterValue[], tabs: FlexTab[]): FlexTab | null {
   for (const tab of tabs) {
      if (filtersMatch(filters, tab.filters)) {
         return tab;
      }
   }
   return null;
}

interface FlexData {
   data: any[];
   total: number;
   loading: boolean;
   pages: any[][] | null;
   requestId?: number;
}

interface FlexSelection {
   selected: string[];
   selectAll: number | undefined;
}

// Split FlexSettings into two interfaces
interface FlexDataSettings {
   search: string;
   filters: FlexFilterValue[];
   sort: string;
}

interface FlexColumnsSettings {
   columns: FlexListColumn<any>[];
   columnsVisible?: string[];
   defaultColumnsVisible?: string[]; // Tab-specific default columns
}

function createCleanableAtomFamily<T extends object>(defaultValue: T) {
   return atomFamily((globalId: string) => {
      const baseAtom = atom<T>(defaultValue);

      baseAtom.onMount = (setAtom) => {
         setAtom((prev) => prev);
         return () => {
            setAtom(defaultValue);
         };
      };

      return baseAtom;
   });
}

// Create atoms with cleanup
export const flexDataAtom = createCleanableAtomFamily<FlexData>({
   data: [],
   total: 0,
   loading: true,
   pages: null
});

export const flexSelectionAtom = createCleanableAtomFamily<FlexSelection>({
   selected: [],
   selectAll: undefined
});

// Split flexSettingsAtom into two separate atoms
export const flexDataSettingsAtom = createCleanableAtomFamily<FlexDataSettings>({
   search: '',
   filters: [],
   sort: ''
});

export const flexColumnsSettingsAtom = createCleanableAtomFamily<FlexColumnsSettings>({
   columns: [],
   columnsVisible: undefined
});

// Update the columns atom to use the new flexColumnsSettingsAtom
export const flexListColumnsAtom = atomFamily((globalId: string) => {
   const baseAtom = flexColumnsSettingsAtom(globalId);

   return atom((get) => {
      const columnsSettings = get(baseAtom);
      const columns = columnsSettings?.columns?.filter(column => {
         if (columnsSettings.columnsVisible) {
            return columnsSettings.columnsVisible.includes(column.id);
         }
         return true;
      }) || [];

      const result: { column: FlexListColumn<any>; width: number; }[] = [];
      const totalWidth = columns.reduce((a, b) => a + (b.width || 1), 0) || 0;

      if (totalWidth) {
         for (const column of columns) {
            const columnWidth = Math.round((column.width || 1) * 100 / totalWidth);
            result.push({
               column: column,
               width: columnWidth
            });
         }
      }
      return result;
   });
});

// FlexList State Management
type AnchorEL = {
   element: HTMLElement | null;
   command: FlexCommand<any>;
}

interface FlexListState {
   anchorEl: AnchorEL | null;
   loadingMore: boolean;
   tabs: FlexTab[];
   tabCountsLoaded: boolean;
   activeTab: string;
   isCustomTab: boolean;
   headerData: FlexHeaderData;
   initialized: boolean;
   executing: string;
}

type FlexListAction =
   | { type: 'INITIALIZE'; payload: { tabs: FlexTab[]; activeTab: string; headerData: FlexHeaderData; isCustomTab: boolean } }
   | { type: 'SET_ANCHOR_EL'; payload: AnchorEL | null }
   | { type: 'SET_LOADING_MORE'; payload: boolean }
   | { type: 'UPDATE_TABS'; payload: FlexTab[] }
   | { type: 'UPDATE_TAB_COUNTS'; payload: { tabId: string; count: number }[] }
   | { type: 'SET_TAB_COUNTS_LOADED'; payload: boolean }
   | { type: 'CHANGE_TAB'; payload: { tabId: string; isCustomTab: boolean } }
   | { type: 'SET_HEADER_DATA'; payload: FlexHeaderData }
   | { type: 'UPDATE_HEADER_DATA'; payload: Partial<FlexHeaderData> }
   | { type: 'SET_EXECUTING'; payload: string }
   | { type: 'RESET' };

const initialFlexListState: FlexListState = {
   anchorEl: null,
   loadingMore: false,
   tabs: [],
   tabCountsLoaded: false,
   activeTab: '',
   isCustomTab: false,
   headerData: {
      search: '',
      sort: '',
      filterAnd: {},
      filterOr: {}
   },
   initialized: false,
   executing: ''
};

function flexListReducer(state: FlexListState, action: FlexListAction): FlexListState {
   switch (action.type) {
      case 'INITIALIZE':
         return {
            ...state,
            tabs: action.payload.tabs,
            activeTab: action.payload.activeTab,
            isCustomTab: action.payload.isCustomTab,
            headerData: action.payload.headerData,
            initialized: true
         };
      case 'SET_ANCHOR_EL':
         return { ...state, anchorEl: action.payload };
      case 'SET_LOADING_MORE':
         return { ...state, loadingMore: action.payload };
      case 'UPDATE_TABS':
         return { ...state, tabs: action.payload, tabCountsLoaded: false };
      case 'UPDATE_TAB_COUNTS':
         return {
            ...state,
            tabs: state.tabs.map(tab => {
               const update = action.payload.find(u => u.tabId === tab.id);
               return update ? { ...tab, count: update.count } : tab;
            })
         };
      case 'SET_TAB_COUNTS_LOADED':
         return { ...state, tabCountsLoaded: action.payload };
      case 'CHANGE_TAB':
         return { ...state, activeTab: action.payload.tabId, isCustomTab: action.payload.isCustomTab };
      case 'SET_HEADER_DATA':
         return { ...state, headerData: action.payload };
      case 'UPDATE_HEADER_DATA':
         return { ...state, headerData: { ...state.headerData, ...action.payload } };
      case 'SET_EXECUTING':
         return { ...state, executing: action.payload };
      case 'RESET':
         return initialFlexListState;
      default:
         return state;
   }
}

export const FlexList = <T extends object>(
   {
      globalId,
      columns,
      itemsPerPage,
      onRequestData,
      options,
      filters,
      sorts,
      tabs: origTabs,
      context,
      selections,
      commands,
      onTabChange,
      onSelectRow,
      initialTab,
      initialSearch,
      initialFilters,
      onSearchChange
   }: FlexListProps<T>) => {

   const [searchParams, setSearchParams] = useSearchParams();
   const initialFilterObjects = initialFilters ? filtersToFilterObjects(initialFilters) : { filterAnd: {}, filterOr: {} };
   const urlParams = options?.disableUrlState
      ? {search: initialSearch || '', filterAnd: initialFilterObjects.filterAnd, filterOr: initialFilterObjects.filterOr, filters: initialFilters || [], tabId: initialTab, sort: ''}
      : applyURLParamsToFlexList(searchParams);

   // Initialize reducer with URL params
   const [state, dispatch] = useReducer(flexListReducer, {
      ...initialFlexListState,
      tabs: origTabs || [],
      activeTab: urlParams.tabId || initialTab || '',
      headerData: {
         search: urlParams.search || initialSearch || '',
         sort: sorts?.find(sort => sort.value === urlParams.sort)?.id || '',
         filterAnd: urlParams.filterAnd || {},
         filterOr: urlParams.filterOr || {}
      }
   });

   const latestRequestIdRef = useRef<number>(0);

   const [flexData, setFlexData] = useAtom(flexDataAtom(globalId));
   const [selection, setSelection] = useAtom(flexSelectionAtom(globalId));

   // Use separate atoms for data settings and column settings
   const [dataSettings, setDataSettings] = useAtom(flexDataSettingsAtom(globalId));
   const [columnsSettings, setColumnsSettings] = useAtom(flexColumnsSettingsAtom(globalId));
   const visibleColumns = useAtomValue(flexListColumnsAtom(globalId));

   const classes = useStyles();

   // Update tabs when origTabs prop changes
   useEffect(() => {
      dispatch({ type: 'UPDATE_TABS', payload: origTabs || [] });
   }, [origTabs]);

   const loadData = useCallback(async () => {
      if (flexData.loading || state.loadingMore || (options?.maxRows && flexData.data.length >= options.maxRows)) return;
      if (flexData.data.length >= flexData.total) return;

      const currentRequestId = latestRequestIdRef.current;
      const nextPage = Math.floor(flexData.data.length / itemsPerPage) + 1;

      try {
         dispatch({ type: 'SET_LOADING_MORE', payload: true });

         const pageData = await onRequestData(
            dataSettings.search,
            dataSettings.sort,
            nextPage,
            buildGraphQLFilter(filters, dataSettings.filters)
         );

         setFlexData(prev => {
            if (latestRequestIdRef.current !== currentRequestId) {
               return prev;
            }

            const newPages = [...prev.pages!];
            newPages[nextPage] = pageData.data;
            return {
               ...prev,
               pages: newPages,
               total: pageData.totalCount,
               data: newPages.flat().filter(Boolean),
               requestId: currentRequestId
            };
         });
      } catch (error) {
         console.error('Error loading more data:', error);
      } finally {
         dispatch({ type: 'SET_LOADING_MORE', payload: false });
      }
      // eslint-disable-next-line
   }, [flexData.pages, flexData.total, dataSettings, globalId, options?.maxRows, state.loadingMore]);

   // Only trigger data reset when data settings change
   useEffect(() => {
      if (!state.initialized) return;

      setFlexData({
         data: [],
         total: 0,
         loading: true,
         pages: null
      });
      setSelection({
         selected: [],
         selectAll: undefined
      });
      // eslint-disable-next-line
   }, [dataSettings, globalId]);

   // Only fetch data when data settings change or during initialization
   useEffect(() => {
      if (!flexData.loading || !state.initialized) return;

      const currentRequestId = Date.now();
      latestRequestIdRef.current = currentRequestId;

      const fetchData = async () => {
         try {
            const pageData = await onRequestData(
               dataSettings.search,
               dataSettings.sort,
               1,
               buildGraphQLFilter(filters, dataSettings.filters)
            );

            setFlexData(prev => {
               if (latestRequestIdRef.current !== currentRequestId) {
                  return prev;
               }

               return {
                  pages: [pageData.data],
                  total: pageData.totalCount,
                  data: pageData.data,
                  loading: false,
                  requestId: currentRequestId
               };
            });
         } catch (error) {
            console.error('Error loading data:', error);
            setFlexData(prev => {
               if (latestRequestIdRef.current !== currentRequestId) {
                  return prev;
               }
               return {...prev, loading: false};
            });
         }
      };

      fetchData();
      // eslint-disable-next-line
   }, [dataSettings, globalId, state.initialized]);

   useEffect(() => {
      if (selection.selectAll !== undefined) {
         if (flexData.data.length === flexData.total) {
            const newSelected = flexData.data.map((row) => row?.[idField]);
            setSelection({
               selected: newSelected,
               selectAll: undefined,
            });
         } else {
            const handle = setTimeout(async () => {
               await loadData();
               if (flexData.data.length < flexData.total) {
                  setSelection({
                     ...selection,
                     selectAll: (selection.selectAll || 0) + 1,
                  });
               } else {
                  const newSelected = flexData.data.map((row) => row?.[idField]);
                  setSelection({
                     selected: newSelected,
                     selectAll: undefined,
                  });
               }
            }, 100);
            return () => clearTimeout(handle);
         }
      }
      // eslint-disable-next-line
   }, [selection.selectAll, flexData, options?.idField, loadData, setSelection]);

   // Initialize both data and column settings separately
   useEffect(() => {
      if (!state.initialized && columns) {
         let selectedTab: FlexTab | undefined;
         let isCustomTab = false;
         let effectiveFilters: FlexFilterValue[];

         // Determine tab and filters based on URL state
         if (urlParams.filters.length > 0) {
            // URL has filters - try to find a matching tab
            const matchingTab = findMatchingTab(urlParams.filters, state.tabs || []);
            if (matchingTab) {
               // Filters match an existing tab
               selectedTab = matchingTab;
               effectiveFilters = matchingTab.filters;
               isCustomTab = false;
            } else {
               // No tab matches - this is a custom filter set
               // Try to find a tab by URL tabId, or fallback to first tab with empty filters
               if (urlParams.tabId && urlParams.tabId !== CUSTOM_TAB_ID && state.tabs) {
                  selectedTab = state.tabs.find(tab => tab.id === urlParams.tabId);
               }
               if (!selectedTab && state.tabs) {
                  selectedTab = state.tabs.find(tab => tab.filters.length === 0);
               }
               effectiveFilters = urlParams.filters;
               isCustomTab = true;
            }
         } else {
            // No filters in URL - use tab-based logic
            if (urlParams.tabId && urlParams.tabId !== CUSTOM_TAB_ID && state.tabs) {
               selectedTab = state.tabs.find(tab => tab.id === urlParams.tabId);
            } else if (initialTab && state.tabs) {
               selectedTab = state.tabs.find(tab => tab.id === initialTab);
            }
            if (!selectedTab && state.tabs) {
               selectedTab = state.tabs.find(tab => tab.filters.length === 0);
            }
            effectiveFilters = selectedTab?.filters || [];
            isCustomTab = false;
         }

         // Load persisted columns (highest priority)
         const persistedColumns = loadCustomColumns(globalId);

         // Priority: Persisted columns > Tab columns > All columns
         const effectiveColumns = persistedColumns || selectedTab?.columns;

         // Set column settings
         setColumnsSettings({
            columns: columns,
            columnsVisible: effectiveColumns,
            defaultColumnsVisible: selectedTab?.columns // Store tab's default for reset
         });

         // Set data settings
         setDataSettings({
            filters: effectiveFilters,
            search: urlParams.search,
            sort: urlParams.sort || ''
         });

         // Convert filters to headerData format for FlexHeader
         const effectiveFilterOr: any = {};
         const effectiveFilterAnd: any = {};

         for (const filter of effectiveFilters) {
            if (filter.requireAll) {
               effectiveFilterAnd[filter.filterId] = filter.values;
            } else {
               effectiveFilterOr[filter.filterId] = filter.values;
            }
         }

         const initialHeaderData: FlexHeaderData = {
            search: urlParams.search || '',
            filterAnd: effectiveFilterAnd,
            filterOr: effectiveFilterOr,
            sort: sorts?.find(sort => sort.value === urlParams.sort)?.id || '',
         };

         const activeTabId = isCustomTab ? CUSTOM_TAB_ID : (selectedTab?.id || state.activeTab);

         if (selectedTab && !isCustomTab) {
            onTabChange?.(selectedTab.id);
         }

         // Update URL to be clean (only serialize filters if custom tab)
         if (!options?.disableUrlState) {
            const newParams = updateSearchParams(
               searchParams,
               getFlexListURLParams(
                  urlParams.search,
                  isCustomTab ? CUSTOM_TAB_ID : (selectedTab?.id || ''),
                  isCustomTab ? effectiveFilters : [], // Only serialize filters for custom tab
                  urlParams.sort || ''
               )
            );
            setSearchParams(newParams, {replace: true});
         }

         // Mark as initialized with proper headerData
         dispatch({
            type: 'INITIALIZE',
            payload: {
               tabs: state.tabs,
               activeTab: activeTabId,
               isCustomTab: isCustomTab,
               headerData: initialHeaderData
            }
         });
      }
      // eslint-disable-next-line
   }, [columns, state.initialized]);

   // Load tab counts only once when component initializes
   useEffect(() => {
      if (state.tabCountsLoaded || !state.initialized || state.tabs.length === 0) return;

      const loadTabCounts = async () => {
         try {
            const tabUpdates: { tabId: string; count: number }[] = [];

            for (const tab of state.tabs) {
               if (tab.showCount) {
                  const tabCount = await onRequestData(
                     "",
                     "",
                     1,
                     buildGraphQLFilter(filters, tab.filters),
                     1
                  );
                  tabUpdates.push({ tabId: tab.id, count: tabCount.totalCount });
               }
            }

            dispatch({ type: 'UPDATE_TAB_COUNTS', payload: tabUpdates });
            dispatch({ type: 'SET_TAB_COUNTS_LOADED', payload: true });
         } catch (error) {
            console.error('Error loading tab counts:', error);
         }
      };

      loadTabCounts();
      // eslint-disable-next-line
   }, [state.initialized]);

   useEffect(() => {
      return PubSub.on('FlexList-DataChange', (updatedData: any) => {
         const id = updatedData?.[idField];
         if (!id) return;

         setFlexData(prev => {
            const findData = prev.data.find((row) => row[idField] === id);
            if (!findData) return prev;

            const newData = prev.data.map((row) =>
               row[idField] === id ? {...row, ...updatedData} : row
            );

            return {
               ...prev,
               data: newData
            };
         });
      });
      // eslint-disable-next-line
   }, [flexData]);

   // Track if we're in the middle of a header change to prevent sync loops
   const headerChangeInProgressRef = useRef(false);

   // Initialize headerData from dataSettings instead of listState
   // This should only run when dataSettings changes externally (e.g., tab change),
   // NOT when it changes from onHeaderChanged (which already updates headerData)
   useEffect(() => {
      if (!state.initialized) return;

      // Skip if we're in the middle of a header change - the header is already updated
      if (headerChangeInProgressRef.current) {
         headerChangeInProgressRef.current = false;
         return;
      }

      const effectiveFilterOr: any = {};
      const effectiveFilterAnd: any = {};

      for (const filter of dataSettings.filters || []) {
         if (filter.requireAll) {
            effectiveFilterAnd[filter.filterId] = filter.values;
         } else {
            effectiveFilterOr[filter.filterId] = filter.values;
         }
      }

      dispatch({
         type: 'SET_HEADER_DATA',
         payload: {
            search: dataSettings.search || '',
            filterAnd: effectiveFilterAnd,
            filterOr: effectiveFilterOr,
            sort: sorts?.find(sort => sort.value === dataSettings.sort)?.id || '',
         }
      });
   }, [dataSettings, sorts, state.initialized]);

   const onHeaderChanged = (newHeaderData: FlexHeaderData) => {
      const sortValue = sorts?.find(sort => sort.id === newHeaderData.sort)?.value;

      const resultFilters: FlexFilterValue[] = [];

      for (const key of Object.keys(newHeaderData.filterAnd)) {
         let selected = newHeaderData.filterAnd[key];
         if (selected?.length > 0) {
            resultFilters.push({
               filterId: key,
               values: selected,
               requireAll: true
            });
         }
      }

      for (const key of Object.keys(newHeaderData.filterOr)) {
         let selected = newHeaderData.filterOr[key];
         if (selected?.length > 0) {
            resultFilters.push({
               filterId: key,
               values: selected,
               requireAll: false
            });
         }
      }

      // Check if the new filters match any existing tab
      const matchingTab = findMatchingTab(resultFilters, state.tabs || []);
      const isCustom = !matchingTab;
      const newActiveTab = matchingTab ? matchingTab.id : CUSTOM_TAB_ID;

      dispatch({
         type: 'UPDATE_HEADER_DATA',
         payload: {
            filterAnd: newHeaderData.filterAnd,
            filterOr: newHeaderData.filterOr,
            sort: newHeaderData.sort,
            search: newHeaderData.search
         }
      });

      // Update tab state if it changed
      if (newActiveTab !== state.activeTab) {
         dispatch({ type: 'CHANGE_TAB', payload: { tabId: newActiveTab, isCustomTab: isCustom } });
         if (matchingTab) {
            onTabChange?.(matchingTab.id);
         }
      }

      // Mark that we're changing from header - prevents the sync useEffect from overwriting
      headerChangeInProgressRef.current = true;

      // Update data settings only
      setDataSettings(prevState => ({
         ...prevState,
         search: newHeaderData.search,
         sort: sortValue,
         filters: resultFilters,
      }));

      // Notify parent component of search change
      if (onSearchChange) {
         onSearchChange(newHeaderData.search);
      }

      // Update URL - only serialize filters if on custom tab
      if (!options?.disableUrlState) {
         const newParams = updateSearchParams(
            searchParams,
            getFlexListURLParams(
               newHeaderData.search,
               newActiveTab,
               isCustom ? resultFilters : [], // Only serialize filters for custom tab
               sortValue
            )
         );
         setSearchParams(newParams, {replace: true});
      }

      setFlexData(prev => ({
         ...prev,
         loading: true,
         pages: null
      }));
   };

   const onSelectAllClick = async (selectAll: boolean) => {
      if (selectAll) {
         setSelection({
            ...selection,
            selectAll: 1
         });
      } else {
         setSelection({
            ...selection,
            selected: [],
            selectAll: undefined
         });
      }
   };

   const handleTabChange = (newTabId: string) => {
      // If clicking on Custom tab, do nothing (already showing custom filters)
      if (newTabId === CUSTOM_TAB_ID) {
         return;
      }

      const newTab = state.tabs?.find(tab => tab.id === newTabId);

      if (newTab) {
         // Clear custom tab state and switch to the selected tab
         dispatch({ type: 'CHANGE_TAB', payload: { tabId: newTabId, isCustomTab: false } });

         // Update data settings and capture the current values for URL update
         let currentSearch = dataSettings.search;
         let currentSort = dataSettings.sort;

         setDataSettings(prev => {
            currentSearch = prev.search;
            currentSort = prev.sort;
            return {
               ...prev,
               filters: newTab.filters || []
            };
         });

         // Load persisted columns (highest priority)
         const persistedColumns = loadCustomColumns(globalId);

         // Priority: Persisted columns > Tab columns
         // Only update columns if there are no persisted customizations
         setColumnsSettings(prev => ({
            ...prev,
            columnsVisible: persistedColumns || newTab.columns,
            defaultColumnsVisible: newTab.columns // Store tab's default for reset
         }));

         // Update URL - clear filters param since tab implies filters
         if (!options?.disableUrlState) {
            const newParams = updateSearchParams(
               searchParams,
               getFlexListURLParams(
                  currentSearch,
                  newTabId,
                  [], // Don't serialize tab's predefined filters
                  currentSort
               )
            );
            setSearchParams(newParams, {replace: true});
         }

         setFlexData(prev => ({
            ...prev,
            loading: true,
            pages: null
         }));

         onTabChange?.(newTabId);
      }
   };

   const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
      const selectedIndex = selection.selected.indexOf(id);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
         newSelected = newSelected.concat(selection.selected, id);
      } else if (selectedIndex === 0) {
         newSelected = newSelected.concat(selection.selected.slice(1));
      } else if (selectedIndex === selection.selected.length - 1) {
         newSelected = newSelected.concat(selection.selected.slice(0, -1));
      } else if (selectedIndex > 0) {
         newSelected = newSelected.concat(
            selection.selected.slice(0, selectedIndex),
            selection.selected.slice(selectedIndex + 1),
         );
      }

      setSelection({
         ...selection,
         selected: newSelected
      });
   };

   const onExecute = async (cmd: FlexCommand<T>) => {
      dispatch({ type: 'SET_EXECUTING', payload: cmd.id });

      if (cmd.execute) {
         const selectedRows = flexData.data.filter(row =>
            selection.selected.includes(row[idField])
         );
         await cmd.execute(selectedRows);
      }

      setFlexData(prev => ({
         ...prev,
         pages: null,
         loading: true
      }));

      await loadData();
      PubSub.emit('FlexList-SearchChanged-' + globalId, dataSettings);
      dispatch({ type: 'SET_EXECUTING', payload: '' });
   };

   const isSelected = (id: string) => selection.selected.includes(id);
   if (!columnsSettings.columns) {
      return <LinearProgress/>
   }

   const idField = options?.idField || '_id'

   const commandOpen = Boolean(state.anchorEl);

   const commandHandleClick = (cmd: FlexCommand<T>, event: React.MouseEvent<HTMLButtonElement>) => {
      dispatch({
         type: 'SET_ANCHOR_EL',
         payload: {
            command: cmd,
            element: event.currentTarget
         }
      });
   };
   const commandHandleClose = () => {
      dispatch({ type: 'SET_ANCHOR_EL', payload: null });
   };

   const isAllSelected = selection.selected.length === flexData.total && flexData.total > 0;
   const isIndeterminate = selection.selected.length > 0 && selection.selected.length < flexData.total;

   if (!state.initialized) {
      return <Box></Box>
   }

   return <div className={classes.flexListContainer}>
      <Dialog open={selection.selectAll !== undefined} maxWidth={"xs"}>
         <DialogContent style={{textAlign: 'center', display: 'flex', flexDirection: 'column'}}>
            <div><CircularProgress/></div>
            <div>Selecting rows, please wait...</div>
         </DialogContent>
      </Dialog>

      {!options?.hideHeader && state.tabs && <div className={classes.flexTab}>
         <FlexTabs
            tabs={[
               ...state.tabs.map(tab => ({
                  ...tab,
                  count: tab.count
               })),
               // Add Custom tab if we're showing custom filters
               ...(state.isCustomTab ? [{
                  id: CUSTOM_TAB_ID,
                  label: 'Custom',
                  filters: [],
                  showCount: false
               }] : [])
            ]}
            tabId={state.activeTab}
            onTabChange={handleTabChange}
         />
      </div>}
      {!options?.hideHeader && (
         <div className={classes.flexHeader}>
            <FlexHeader
               globalId={globalId}
               count={flexData.total}
               filters={filters}
               sorts={sorts}
               searchLabel={options?.searchLabel}
               style={{display: selection.selected.length > 0 ? 'none' : 'block'}}
               headerData={state.headerData}
               onHeaderChanged={onHeaderChanged}
               isSearchLoading={flexData.loading}
            />
            <div className={classes.flexCommand} style={{display: selection.selected.length > 0 ? 'flex' : 'none'}}>
               <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
                  {selection.selected.length} selected
                  <IconButton
                     size="small"
                     onClick={() => setSelection(prev => ({...prev, selected: []}))}>
                     <ClearIcon fontSize="inherit"/>
                  </IconButton>
               </Typography>
               <div className={classes.commands}>
                  {commands?.map(cmd => {
                     const onClick = async () => await onExecute(cmd);
                     return (
                        <Box key={'command_' + cmd.id}>
                           <Tooltip title={cmd.title} key={'cmd_' + cmd.id}>
                              <Button
                                 aria-label={cmd.title}
                                 onClick={cmd.children ? (e) => commandHandleClick(cmd, e) : onClick}
                                 size="small"
                                 startIcon={
                                    cmd.children ? undefined :
                                       state.executing === cmd.id ? <CircularProgress size={18}/> :
                                          <cmd.icon/>
                                 }
                                 endIcon={cmd.children ? <ArrowDropDownIcon/> : undefined}
                              >
                                 {cmd.title}
                              </Button>
                           </Tooltip>
                        </Box>
                     );
                  })}
               </div>
            </div>
         </div>
      )}
      {state.anchorEl && <Menu id={"menu_" + state.anchorEl.command!.id} open={commandOpen} anchorEl={state.anchorEl.element}
                         onClose={commandHandleClose}>
         {state.anchorEl.command!.children!.map(childCmd => {
            return <MenuItem key={'cmd_' + childCmd.id}
                             onClick={async () => await onExecute(childCmd)}>
               <ListItemIcon>
                  {state.executing === childCmd.id ? <CircularProgress size={18}/> : <childCmd.icon/>}
               </ListItemIcon>
               <ListItemText>
                  {childCmd.title}
               </ListItemText>
            </MenuItem>
         })}
      </Menu>}

      <div className={classes.flexContent}>
         <TableVirtuoso
            data={flexData.data}
            endReached={loadData}
            fixedItemHeight={options?.itemHeight}
            useWindowScroll={!options?.height}
            style={{width: '100%', height: options?.height}}
            className={classes.table}
            increaseViewportBy={options?.height ? options.height : 800}
            overscan={60}
            fixedHeaderContent={() => {
               return <tr>
                  {selections && <td className={classes.headerCell}
                                     width={'40px'}
                                     key={'select_all_column'}>
                     <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={async (e) => await onSelectAllClick(e.target.checked)}
                        inputProps={{'aria-label': 'select all data'}}
                     />
                  </td>}
                  {visibleColumns.map((column, index) => {
                     return <td
                        className={classes.headerCell}
                        width={column.width + '%'}
                        key={'header_' + column.column.id + '_' + index}>
                        {column.column.label}
                     </td>
                  })}</tr>
            }}
            itemContent={(index: number, row: any) => {
               if (!row) {
                  return <></>
               }
               let id = row[idField];
               if (!row) {
                  return <></>
               }
               const isItemSelected = isSelected(id);
               const labelId = `enhanced-table-checkbox-${index}`;
               return <>
                  {selections && <td className={classes.dataCell}
                                     width={'40px'}
                                     key={'select_all_column'}>
                     <Checkbox
                        onClick={(event) => {
                           event.preventDefault();
                           event.stopPropagation();
                           handleClick(event, id)
                        }}
                        checked={isItemSelected}
                        inputProps={{'aria-labelledby': labelId}}
                     />
                  </td>}

                  {visibleColumns.map((visibleColumn, colindex) => {
                     const column = visibleColumn.column;
                     const value = getCellValue(column, row);
                     let innerComponent;
                     if (column.render !== undefined) {
                        innerComponent = <>{column.render(value, row, context)}</>
                     } else {
                        innerComponent = <>{value}</>
                     }
                     let title: string | undefined;
                     if (typeof value === 'string') {
                        title = String(value);
                     }

                     return <td
                        onClick={(e) => {
                           e.preventDefault();
                           e.stopPropagation();
                           onSelectRow && onSelectRow(row, e.ctrlKey)
                        }}
                        className={classes.dataCell}
                        width={visibleColumn.width + '%'}
                        title={title}
                        key={"r_" + id + "_" + column.id + "_" + colindex}
                        style={{height: options?.itemHeight, cursor: onSelectRow ? 'pointer' : undefined}}>
                        {column.ellipsis ?
                           <div style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              marginRight: '15px'
                           }}>
                              {innerComponent}
                           </div>
                           : <>
                              {innerComponent}
                           </>
                        }
                     </td>
                  })}</>
            }}
            fixedFooterContent={() => {
               return <tr>
                  <td colSpan={visibleColumns.length + 1}>
                     {(flexData.loading || state.loadingMore) && <LinearProgress/>}
                  </td>
               </tr>
            }}
         >
         </TableVirtuoso>
      </div>
   </div>
}


/**
 * Responsible for getting cell value given a column and a row.
 * @param column The column
 * @param row The row
 */
const getCellValue = (column: FlexListColumn<any>, row: any) => {
   if (column.get !== undefined) {
      return column.get(row)
   } else if (column.field !== undefined) {
      return row[column.field];
   } else {
      throw new Error('Column "' + column.label + '" must have either get or field');
   }
}

const useStyles = makeStyles({
   flexListContainer: {
      display: 'flex',
      flexDirection: 'column'
   },
   flexTab: {
      marginBottom: '3px',
   },
   flexHeader: {},
   flexContent: {
      marginTop: '4px',
      "& tr:hover": {
         transition: 'all .2s ease',
         backgroundColor: '#F4F4F4'
      }
   },
   table: {
      "& table": {
         tableLayout: "fixed",
         width: '100%',
         overflow: "hidden",
         wordWrap: "break-word"
      }
   },
   commands: {
      display: 'flex',
      flexDirection: 'row',
      gap: '10px'
   },
   headerCell: {
      fontWeight: 600,
      backgroundColor: '#F4F4F4'
   },
   dataCell: {
      borderTop: '1px solid #e0e0e0'
   },
   title: {},
   flexCommand: {
      display: 'flex',
      justifyContent: 'space-between',
      backgroundColor: '#f8f8f8',
      paddingTop: '14px',
      paddingBottom: '14px'
   }
});